import { z } from 'zod'
import { IsoDateTimeSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'
import { ProviderFlowSchema } from './providers'

/**
 * Face-check attempts (spec E4, E7, E10.5, E10.6; R27, R31; AC1, AC3, AC21, AC30): start a hosted or embedded-SDK
 * attempt, the once-per-attempt browser credentials of embedded flows, and the "the browser finished" /
 * "the browser failed" signals that only pull the collect job forward (they never wait for the provider).
 * Own-capture providers start through the capture endpoints (Task 12).
 */

/** Same values as the API's `biometric_attempts.status` (apps/api checks they stay equal at compile time). */
export const ATTEMPT_STATUS_VALUES = ['creating', 'pending', 'final', 'start_failed', 'superseded'] as const
export const AttemptStatusSchema = z.enum(ATTEMPT_STATUS_VALUES)

export const StartAttemptBodySchema = z.object({
  /** The S4 biometric consent box (separate, unticked by default; it names the provider). */
  consentBiometric: z.literal(true),
  /** Embedded flows with flashing lights: the member answered "yes" to the flashing-lights question (E10.4). */
  noFlash: z.boolean().optional(),
})
export type StartAttemptBody = z.infer<typeof StartAttemptBodySchema>

export const StartAttemptResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('redirect'), attemptId: UuidSchema, url: z.string() }),
  z.object({
    kind: z.literal('embedded'),
    attemptId: UuidSchema,
    /** Provider-specific parameters of the browser component (e.g. the session id). */
    params: z.record(z.string(), z.unknown()),
    /** The provider's result window: the browser counts down from serverNow to deadline. */
    deadline: IsoDateTimeSchema,
    serverNow: IsoDateTimeSchema,
  }),
])
export type StartAttemptResponse = z.infer<typeof StartAttemptResponseSchema>

export const AttemptIdParamsSchema = z.object({ id: UuidSchema })

/** Short-lived browser credentials (AWS STS: 15 min). Never cached (Cache-Control: no-store), never logged. */
export const ClientCredentialsResponseSchema = z.object({
  credentials: z.object({
    accessKeyId: z.string(),
    secretAccessKey: z.string(),
    sessionToken: z.string(),
    expiration: z.string(),
  }),
})
export type ClientCredentialsResponse = z.infer<typeof ClientCredentialsResponseSchema>

/** The browser component's error state (stored as `<PROVIDER>_CLIENT_<STATE>`, upper case, at most 40 characters). */
export const ClientErrorBodySchema = z.object({
  state: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{1,40}$/),
})

/**
 * T23 (E12.4): one opaque blob of a relay-style embedded SDK (FaceTec), passed to the provider by our API. The blobs
 * are never logged, stored or put in a job payload.
 */
export const RelayBodySchema = z.object({
  requestBlob: z.string().min(1).max(1_000_000),
  /** Sent on as HTTP header values: printable ASCII only. */
  xUserAgent: z
    .string()
    .max(1000)
    .regex(/^[\x20-\x7E]*$/),
  testingApiHeader: z
    .string()
    .max(4000)
    .regex(/^[\x20-\x7E]*$/)
    .nullable(),
})
export type RelayBody = z.infer<typeof RelayBodySchema>
export const RelayResponseSchema = z.object({ responseBlob: z.string() })

/** 202: the attempt's state when the signal was recorded (the decision arrives through the status endpoint). */
export const CollectSignalResponseSchema = z.object({ state: AttemptStatusSchema })

/** The member's latest attempt, as the S4 screens need it (never a score). */
export const AttemptSummarySchema = z.object({
  id: UuidSchema,
  status: AttemptStatusSchema,
  provider: z.string(),
  flow: ProviderFlowSchema.nullable(),
  deadline: IsoDateTimeSchema.nullable(),
  /**
   * Plain hints for the S4 outcome lines: `not_started` (start_failed), `no_decision`, `liveness_failed`,
   * `client_error`, and the provider's warning codes as given (the web app shows a line only for codes it knows).
   */
  outcomeHints: z.array(z.string()),
  /** T23 (E12.5): the liveness is proven and the attempt waits for our own still (the shared camera screen). */
  needsStill: z.boolean().default(false),
  /**
   * R106: the attempt ended without a result because of our own failure after the provider passed the liveness check
   * (its reference image could not be fetched, or our face engine failed). It did not count as a try.
   */
  ourSideFailure: z.boolean().default(false),
})
export type AttemptSummary = z.infer<typeof AttemptSummarySchema>

export const attemptRoutes = {
  attemptStart: route({
    method: 'POST',
    path: '/api/member/attempts',
    auth: 'member_or_registration',
    body: StartAttemptBodySchema,
    response: StartAttemptResponseSchema,
  }),
  attemptCredentials: route({
    method: 'POST',
    path: '/api/member/attempts/:id/credentials',
    auth: 'member_or_registration',
    params: AttemptIdParamsSchema,
    response: ClientCredentialsResponseSchema,
  }),
  attemptComplete: route({
    method: 'POST',
    path: '/api/member/attempts/:id/complete',
    auth: 'member_or_registration',
    params: AttemptIdParamsSchema,
    response: CollectSignalResponseSchema,
  }),
  attemptClientError: route({
    method: 'POST',
    path: '/api/member/attempts/:id/client-error',
    auth: 'member_or_registration',
    params: AttemptIdParamsSchema,
    body: ClientErrorBodySchema,
    response: CollectSignalResponseSchema,
  }),
  /** T23: up to 1 MB, its own JSON parser (mounted with the raw-body routers); Cache-Control: no-store. */
  attemptRelay: route({
    method: 'POST',
    path: '/api/member/attempts/:id/relay',
    auth: 'member_or_registration',
    params: AttemptIdParamsSchema,
    body: RelayBodySchema,
    response: RelayResponseSchema,
  }),
}
